import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Mail, Phone, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const ContactPage = ({ seoSettings }) => {
  const [settings, setSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    event_type: "",
    guest_count: "",
    service_id: "",
    message: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/services`),
      ]);
      setSettings(settingsRes.data);
      setServices(servicesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!date) {
      toast.error("Please select an event date");
      setLoading(false);
      return;
    }

    try {
      const bookingData = {
        ...formData,
        event_date: format(date, "yyyy-MM-dd"),
        guest_count: parseInt(formData.guest_count),
      };

      await axios.post(`${API}/bookings`, bookingData);
      toast.success("Booking request submitted successfully! We'll contact you soon.");
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        phone: "",
        address: "",
        event_type: "",
        guest_count: "",
        service_id: "",
        message: "",
      });
      setDate(undefined);
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Failed to submit booking. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pt-20 lg:pt-[118px] min-h-screen">
      <SEO 
        title={`Contact - ${seoSettings?.seo_title || 'Mélange Mobile Bar'}`}
        description={seoSettings?.seo_description}
        keywords={seoSettings?.seo_keywords}
        ogImage={seoSettings?.seo_og_image}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#F7F4F0] to-[#F7F4F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" data-testid="contact-page-title">
            Get In Touch
          </h1>
          <p className="text-base text-gray-600 max-w-2xl mx-auto">
            Ready to book our services? Fill out the form below or reach out directly
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-testid="contact-info-title">
                Contact Information
              </h2>
              <div className="space-y-6">
                <div className="flex items-start" data-testid="contact-email">
                  <Mail className="text-[#8B7355] mr-4 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <p className="text-gray-600">{settings?.contact_email || "info@elitebar.com"}</p>
                  </div>
                </div>
                <div className="flex items-start" data-testid="contact-phone">
                  <Phone className="text-[#8B7355] mr-4 flex-shrink-0 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <p className="text-gray-600">{settings?.contact_phone || "(555) 123-4567"}</p>
                  </div>
                </div>
                {settings?.contact_address && (
                  <div className="flex items-start" data-testid="contact-address">
                    <MapPin className="text-[#8B7355] mr-4 flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">Address</h3>
                      <p className="text-gray-600">{settings.contact_address}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Social Links */}
              {(settings?.social_facebook || settings?.social_instagram || settings?.social_twitter) && (
                <div className="mt-8">
                  <h3 className="font-semibold text-gray-900 mb-4">Follow Us</h3>
                  <div className="flex space-x-4">
                    {settings.social_facebook && (
                      <a href={settings.social_facebook} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#8B7355]" data-testid="social-facebook">
                        Facebook
                      </a>
                    )}
                    {settings.social_instagram && (
                      <a href={settings.social_instagram} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#8B7355]" data-testid="social-instagram">
                        Instagram
                      </a>
                    )}
                    {settings.social_twitter && (
                      <a href={settings.social_twitter} target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#8B7355]" data-testid="social-twitter">
                        Twitter
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Booking Form */}
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <h2 className="text-3xl font-bold text-gray-900 mb-6" data-testid="booking-form-title">
                Request a Quote
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="booking-form">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    data-testid="booking-form-name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="booking-form-email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    data-testid="booking-form-phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="address">Event Address *</Label>
                  <Input
                    id="address"
                    name="address"
                    data-testid="booking-form-address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="Event venue address"
                    required
                  />
                </div>
                <div>
                  <Label>Event Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal"
                        data-testid="booking-form-date-picker"
                      >
                        {date ? format(date, "PPP") : "Pick a date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={setDate}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label htmlFor="event_type">Event Type *</Label>
                  <Select
                    name="event_type"
                    onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                    value={formData.event_type}
                  >
                    <SelectTrigger data-testid="booking-form-event-type">
                      <SelectValue placeholder="Select event type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="wedding">Wedding</SelectItem>
                      <SelectItem value="corporate">Corporate Event</SelectItem>
                      <SelectItem value="birthday">Birthday Party</SelectItem>
                      <SelectItem value="private">Private Party</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="guest_count">Estimated Guest Count *</Label>
                  <Input
                    id="guest_count"
                    name="guest_count"
                    type="number"
                    data-testid="booking-form-guest-count"
                    value={formData.guest_count}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="service_id">Preferred Service Package</Label>
                  <Select
                    name="service_id"
                    onValueChange={(value) => setFormData({ ...formData, service_id: value })}
                    value={formData.service_id}
                  >
                    <SelectTrigger data-testid="booking-form-service">
                      <SelectValue placeholder="Select a package (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id}>
                          {service.name} - {service.price}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Additional Details</Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={4}
                    data-testid="booking-form-message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us more about your event..."
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="booking-form-submit"
                >
                  {loading ? "Submitting..." : "Submit Request"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
